import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";

import {
  useCreateSprint,
  useUpdateSprint,
} from "@/application/use-cases/sprints/use-sprints.queries";
import { Field } from "@/presentation/components/delivery-tasks/field";
import {
  formatSprintRange,
  weekEndFor,
} from "@/presentation/components/delivery-tasks/sprint-dates";
import { useAuth } from "@/presentation/providers/auth.provider";
import type { SprintRow } from "@/infrastructure/supabase/database.types";

/**
 * Create or rename/reschedule a sprint — one form for both, since the fields are
 * identical and only the submit differs. Pass `sprint` to edit, `null` to create.
 *
 * Creating: the name and start date arrive prefilled and the end date follows
 * the start a week later until it is touched, so the usual case is
 * open-and-confirm. Editing: all three fields hold the stored values, and the
 * end date stays put when the start moves (an explicit helper re-derives the
 * week), so a reschedule can't silently change a sprint's length.
 *
 * Mounted only while open, so state is seeded from fresh props each time.
 */
export function SprintDialog({
  sprint,
  defaultName,
  defaultStart,
  onClose,
}: {
  sprint: SprintRow | null;
  defaultName: string;
  defaultStart: string;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const create = useCreateSprint();
  const update = useUpdateSprint();
  const isEdit = sprint !== null;

  const [name, setName] = useState(sprint?.name ?? defaultName);
  const [start, setStart] = useState(sprint?.start_date ?? defaultStart);
  const [end, setEnd] = useState(sprint?.end_date ?? weekEndFor(defaultStart));
  const [endTouched, setEndTouched] = useState(isEdit);
  const [busy, setBusy] = useState(false);

  const rangeInvalid = !!start && !!end && end < start;
  const userId = session?.user?.id ?? null;
  const canSubmit =
    !!name.trim() && !!start && !!end && !rangeInvalid && !busy && (isEdit || !!userId);

  const changeStart = (next: string) => {
    setStart(next);
    // While the end date is still the derived week, keep it glued to the start.
    if (!endTouched && next) setEnd(weekEndFor(next));
  };

  const handleClose = () => {
    setBusy(false);
    onClose();
  };

  const save = async () => {
    if (!canSubmit) return;
    // Which mutation to run is resolved BEFORE the busy flag flips, so the
    // create branch can narrow `userId` (string | null) down to the non-null
    // author the insert policy demands without a bail-out mid-submit leaving
    // the button stuck on "Saving…". `canSubmit` already requires that author
    // on the create path, so the `null` fallthrough is unreachable.
    const submit =
      sprint !== null
        ? async () => {
            await update.mutateAsync({
              id: sprint.id,
              patch: { name: name.trim(), start_date: start, end_date: end },
            });
            return "Sprint updated";
          }
        : userId !== null
          ? async () => {
              await create.mutateAsync({
                name: name.trim(),
                start_date: start,
                end_date: end,
                created_by: userId,
              });
              return "Sprint created";
            }
          : null;
    if (submit === null) return;

    setBusy(true);
    try {
      const message = await submit();
      toast.success(message);
      handleClose();
    } catch (err) {
      setBusy(false);
      toast.error(err instanceof Error ? err.message : "Failed to save sprint");
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit sprint" : "New sprint"}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              autoFocus
              required
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Starts">
              <Input
                type="date"
                value={start}
                onChange={(e) => changeStart(e.target.value)}
                required
              />
            </Field>
            <Field label="Ends">
              <Input
                type="date"
                value={end}
                onChange={(e) => {
                  setEndTouched(true);
                  setEnd(e.target.value);
                }}
                required
              />
            </Field>
          </div>

          {/* The empty-value branch comes first: a cleared date input reads as
              "", and the preview below feeds both values straight to date-fns,
              which throws on an invalid date and would unmount the dialog. */}
          {!start || !end ? (
            <p className="text-destructive text-[11px]">
              Both a start and an end date are required.
            </p>
          ) : rangeInvalid ? (
            <p className="text-destructive text-[11px]">
              The end date can’t come before the start date.
            </p>
          ) : (
            <p className="text-muted-foreground flex items-center gap-2 text-[11px]">
              <span className="text-foreground">{formatSprintRange(start, end)}</span>
              <span>· {dayCount(start, end)} days</span>
              {end !== weekEndFor(start) ? (
                <button
                  type="button"
                  className="text-primary underline underline-offset-2"
                  onClick={() => {
                    setEndTouched(false);
                    setEnd(weekEndFor(start));
                  }}
                >
                  make it one week
                </button>
              ) : null}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create sprint"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Inclusive length, so a Sun–Sat sprint reads as 7 days rather than 6. */
function dayCount(startIso: string, endIso: string): number {
  const ms = Date.parse(`${endIso}T00:00:00Z`) - Date.parse(`${startIso}T00:00:00Z`);
  return Math.round(ms / 86_400_000) + 1;
}
